import type { ContextField } from "@appretio/context-selector";
import {
  Button,
  InputField,
  Label,
  Note,
  Option,
  SingleSelect
} from "@appretio/ui-kit";
import { useMemo, useState } from "react";
import {
  DIMENSION_VALUE_PATTERN,
  DIMENSION_VALUE_RULES,
  getFieldLabel
} from "../../forecastContext";
import { useCloneBrand } from "../../hooks/manageCubes/useCloneBrand";
import { useContextFilters } from "../../hooks/manageCubes/useContextFilters";
import { useCubeContext } from "../../hooks/manageCubes/useCubeContext";
import { useManageCubesOperationToast } from "../../hooks/manageCubes/useManageCubesOperationToast";
import { ConfirmOperationDialog } from "./ConfirmOperationDialog";
import {
  newDimValueError,
  targetsWholeDimension
} from "./dimensionValueValidation";
import {
  buttonRow,
  fieldGroup,
  formColumn,
  hintText,
  inlineRow,
  narrowField
} from "./manageCubesOperations.css";

/**
 * The product hierarchy, broadest first — mirrors the backend `_BRAND_KEYS`.
 * A clone renames from its entry level down, so the order is the semantics.
 */
const HIERARCHY_KEYS: ContextField[] = ["brand", "sub_brand", "product_line"];

type Filters = Record<string, string[]> | undefined;

/** `filters` minus `field`'s own selection, or undefined when nothing remains. */
function withoutField(filters: Filters, field: ContextField): Filters {
  if (!filters) return undefined;
  const others = Object.fromEntries(
    Object.entries(filters).filter(([key]) => key !== field)
  );
  return Object.keys(others).length > 0 ? others : undefined;
}

function nameError(
  raw: string,
  entryKey: ContextField | undefined,
  selections: Record<string, string[]>,
  existing: string[]
): string | undefined {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return undefined;
  if (!DIMENSION_VALUE_PATTERN.test(trimmed)) {
    return `Invalid name. ${DIMENSION_VALUE_RULES}`;
  }
  if (!entryKey) return undefined;
  // The option lists cannot decide a combination, so the live check only
  // runs when nothing else narrows the destination; otherwise the backend
  // guard answers on submit and its reason surfaces in the error toast.
  if (!targetsWholeDimension(selections, entryKey)) return undefined;
  return newDimValueError(existing, trimmed);
}

interface HierarchySelectProps {
  cubeId: string;
  field: ContextField;
  filters: Filters;
  selected: string | undefined;
  onChange: (value: string | undefined) => void;
}

/**
 * One hierarchy level, cross-filtered by the *other* levels only: filtering a
 * level by its own selection would collapse its list to that one value.
 */
function HierarchySelect({
  cubeId,
  field,
  filters,
  selected,
  onChange
}: HierarchySelectProps) {
  const { data, isLoading } = useCubeContext(
    cubeId,
    withoutField(filters, field)
  );
  const options: Option[] = (data?.options?.[field] ?? []).map(
    (value: string) => ({ label: value, value })
  );
  return (
    <div className={`${fieldGroup} ${narrowField}`}>
      <SingleSelect
        label={getFieldLabel(field)}
        options={options}
        selectedOption={
          selected ? { label: selected, value: selected } : undefined
        }
        onChange={(value) => onChange(value || undefined)}
        loading={isLoading}
        size="md"
      />
    </div>
  );
}

/** Props for the Clone operation form. */
interface CloneOperationProps {
  /** The target cube ID to clone within. */
  cubeId: string;
}

export function CloneOperation({ cubeId }: CloneOperationProps) {
  const [newBrand, setNewBrand] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const { showSuccess, ToastElement } = useManageCubesOperationToast();
  const { selections, activeFilters, handleFieldChange, resetSelections } =
    useContextFilters();
  const { data: cubeContext } = useCubeContext(cubeId);
  const cloneBrand = useCloneBrand();

  // The deepest level selected: the one the new name replaces, and the reason
  // the levels above it survive.
  const entryKey = useMemo(
    () => HIERARCHY_KEYS.filter((key) => selections[key]?.length).at(-1),
    [selections]
  );

  // Derived, not stored: the name may be typed before the source is picked.
  const error = useMemo(
    () =>
      nameError(
        newBrand,
        entryKey,
        selections,
        entryKey ? (cubeContext?.options?.[entryKey] ?? []) : []
      ),
    [newBrand, entryKey, selections, cubeContext]
  );

  function handleConfirm() {
    cloneBrand.mutate(
      {
        cube_id: cubeId,
        context: activeFilters ?? {},
        new_brand: newBrand.trim()
      },
      {
        onSuccess: (data) => {
          showSuccess(data.message);
          resetSelections();
          setNewBrand("");
          setDialogOpen(false);
        },
        // The global mutation handler already toasts the failure with the
        // server's reason — a local toast would duplicate it.
        onError: () => {
          setDialogOpen(false);
        }
      }
    );
  }

  const renamed = entryKey
    ? HIERARCHY_KEYS.slice(HIERARCHY_KEYS.indexOf(entryKey))
    : [];
  const inherited = entryKey
    ? HIERARCHY_KEYS.slice(0, HIERARCHY_KEYS.indexOf(entryKey))
    : [];

  return (
    <>
      {ToastElement}
      <div className={formColumn}>
        <p className={hintText}>
          Duplicate a brand, sub-brand or product line under a new name. The
          level you pick and everything below it take the new name; the levels
          above it are kept.
        </p>

        <div className={inlineRow}>
          {HIERARCHY_KEYS.map((key) => (
            <HierarchySelect
              key={key}
              cubeId={cubeId}
              field={key}
              filters={activeFilters}
              selected={selections[key]?.[0]}
              onChange={(value) => handleFieldChange(key, value ? [value] : [])}
            />
          ))}
        </div>

        <div className={`${fieldGroup} ${narrowField}`}>
          <Label label="New name" />
          <InputField
            placeholder="Enter the new name"
            value={newBrand}
            onChange={(e) => setNewBrand(e.target.value)}
            error={error}
          />
        </div>

        {entryKey && (
          <Note
            variant="info"
            size="compact"
            text={
              `Renames ${renamed.map(getFieldLabel).join(", ")} to the new ` +
              `name, summing as they collapse.` +
              (inherited.length
                ? ` Keeps ${inherited.map(getFieldLabel).join(", ")} from the source.`
                : "") +
              " Intake is not copied — a brand-new brand has no orders."
            }
          />
        )}

        <div className={buttonRow}>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setDialogOpen(true)}
            disabled={!entryKey || !newBrand.trim() || !!error}
            title={
              entryKey
                ? undefined
                : "Pick a brand, sub-brand or product line to clone."
            }
          >
            Clone
          </Button>
        </div>
      </div>
      <ConfirmOperationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title="Clone"
        confirmText="Clone"
        onConfirm={handleConfirm}
        isLoading={cloneBrand.isPending}
      >
        <p>
          Clone {entryKey && getFieldLabel(entryKey).toLowerCase()}{" "}
          <strong>{entryKey && selections[entryKey]?.[0]}</strong> as{" "}
          <strong>{newBrand.trim()}</strong>? This operation is irreversible.
        </p>
        {inherited.length > 0 && (
          <p>
            Kept from the source: {inherited.map(getFieldLabel).join(", ")}.
          </p>
        )}
      </ConfirmOperationDialog>
    </>
  );
}
